// CRM E-I / E-IV: eine `Activity` aus dem Browser anlegen.
//
// Der Grund für diesen Helfer ist eine einzige Regel, die an zwei Stellen gebraucht
// wird und an beiden leicht zu vergessen ist: **eine Aktivität muss die drei
// Auth-Felder beim Anlegen mitbringen.** `Activities` trägt Field-Level-`@auth` an
// `userId`, `companyOwnerUserId` und `leadShare` — mit `create` für die
// Vertriebsrollen, aber ohne `update` (S-0 Prüfung 6). Wer sie beim Anlegen weglässt,
// kann sie **nie mehr nachtragen**, und der Datensatz bleibt für den Zuständigen der
// Firma dauerhaft unsichtbar, obwohl er an seiner Firma hängt.
//
// Regel 3: leere optionale Felder werden WEGGELASSEN, nicht auf "" oder " " gesetzt —
// `companyID` ist Partition Key eines GSI, ein Sentinel legte alle ortlosen
// Aktivitäten in dieselbe Partition.

import { client } from "./amplifyClient";
import { withTaskSortKey } from "./tasks";
import {
  createActivities,
  updateTaskAfterActivity,
  getCompanyActivityContext,
  getTaskActivityIDs,
} from "../components/graphql/activities.queries";

/**
 * Firmenbezogene Auth-Felder auflösen. Ohne lesbare Firma bleiben sie leer — die
 * Aktivität entsteht trotzdem und gehört dann nur ihrem Verfasser.
 */
const companyContext = async (companyID) => {
  if (!companyID) return {};
  try {
    const result = await client.graphql({
      query: getCompanyActivityContext,
      variables: { id: companyID },
      skipGlobalError: true,
    });
    const company = result.data?.getCompanies;
    if (!company) return {};
    return {
      ...(company.contactPersonUserId?.trim()
        ? { companyOwnerUserId: company.contactPersonUserId.trim() }
        : {}),
      // A-V Punkt 22: nur am Lead gefüllt. Bei einem Kunden bliebe das Feld leer, und
      // die Gruppenregel greift dann für niemanden.
      ...(company.type === "lead" ? { leadShare: ["AdministrationSalesUser"] } : {}),
    };
  } catch (error) {
    console.log("getCompanyActivityContext", error);
    return {};
  }
};

/**
 * Legt eine Aktivität an und schließt, falls sie aus einer Aufgabe entstand, die
 * Gegenrichtung: die neue ID wird an `Tasks.activityIDs` angehängt, und die Aufgabe
 * geht auf erledigt. Die Aufgabe bleibt vollständig erhalten (E-I) — der Verlauf zeigt
 * nur nicht beides.
 *
 * @param fields  Felder der Aktivität. Leere werden hier entfernt (Regel 3).
 * @param userId  `sub` des angemeldeten Nutzers.
 * @param task    optional die Aufgabe, aus der sie entstand.
 * @param completesTask  ob die Aufgabe damit erledigt ist. `false` bei einem Anruf,
 *                der niemanden erreicht hat: der Anruf hängt an der Aufgabe, aber der
 *                Auftrag ist nicht erfüllt und die Aufgabe bleibt offen (CallOutcomePanel).
 */
export const logActivity = async ({ fields, userId, task, completesTask = true }) => {
  const context = await companyContext(fields.companyID);
  const input = { ...fields, ...context };
  if (userId) input.userId = userId;
  if (task?.id) input.taskID = task.id;
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null || value === "") delete input[key];
  }

  const created = (
    await client.graphql({ query: createActivities, variables: { input } })
  ).data.createActivities;

  await linkTaskActivity(task, created.id, { complete: completesTask });

  return created;
};

// Der Stand der Liste, an den angehaengt wird — siehe unten.
const currentActivityIDs = async (task) => {
  try {
    const result = await client.graphql({
      query: getTaskActivityIDs,
      variables: { id: task.id },
      skipGlobalError: true,
    });
    return result.data?.getTasks?.activityIDs || [];
  } catch (error) {
    console.error("Stand der Aufgaben-Zeiger konnte nicht gelesen werden", error);
    return task.activityIDs || [];
  }
};

/**
 * Die Gegenrichtung einer Aufgabe pflegen: die Aktivitaet ANHAENGEN und die Aufgabe —
 * wenn sie damit erfuellt ist — abhaken. `activityIDs` ist eine Liste, weil derselbe
 * Auftrag mehrere Versuche kosten kann; jeder davon bleibt verknuepft.
 *
 * In `activityIDs` stehen bei einer MAIL-Aufgabe Emails-IDs — das Schema sagt es so
 * ("die Gegenrichtung zu Activities.taskID bzw. Emails.taskID"), und der Aufgabentyp
 * entscheidet, in welcher Tabelle nachgesehen wird. Deshalb hier ohne Meinung darueber,
 * woher die ID kommt.
 *
 * Eigener Aufruf und bewusst NICHT im try des Aufrufers: scheitert er, steht das
 * Ergebnis trotzdem — eine verlorene Aufgabenquittung ist wiederherstellbar, ein
 * verlorenes Gespraech oder eine gesendete Mail nicht. Ohne `activityId` (die Mail ging
 * raus, wurde aber von der Filterkette nicht erfasst) wird die Aufgabe trotzdem
 * abgehakt — getan ist getan, nur der Zeiger fehlt.
 *
 * Angehaengt wird an den FRISCH GELESENEN Stand und nicht an den des Aufrufers: der
 * zweite Anruf auf dieselbe Aufgabe passiert oft ohne Neuladen dazwischen, und der
 * haette den ersten Zeiger sonst ueberschrieben. Scheitert das Lesen, wird an den
 * Stand des Aufrufers angehaengt — ein Zeiger zu viel verlieren ist besser als das
 * Abhaken ausfallen zu lassen.
 *
 * ponytail: kein echtes Sperren, nur ein Lesen kurz davor. Zwei Aktivitaeten in
 * derselben Sekunde koennen sich immer noch ueberholen. Der Anruf selbst zeigt ueber
 * `Activities.taskID` weiter zurueck, verloren ginge also nur der Sprung von der
 * Aufgabe aus. Ceiling: wenn es stoert, `Activities.taskID` einen Index geben und die
 * Liste abschaffen.
 */
export const linkTaskActivity = async (task, activityId, { complete = true } = {}) => {
  if (!task?.id) return;
  const activityIDs = activityId
    ? [...(await currentActivityIDs(task)), activityId]
    : null;
  // Nichts abzuhaken und nichts zu verknuepfen: dann auch kein Update.
  if (!complete && !activityIDs) return;
  try {
    await client.graphql({
      query: updateTaskAfterActivity,
      variables: {
        input: withTaskSortKey(task, {
          id: task.id,
          ...(complete
            ? { status: "completed", completedAt: new Date().toISOString() }
            : {}),
          ...(activityIDs ? { activityIDs } : {}),
        }),
      },
    });
  } catch (error) {
    console.error("Aktivitaet konnte nicht an der Aufgabe vermerkt werden", error);
  }
};
